import { useRouter, type RouteParamsRawGeneric, type RouteRecordRaw } from 'vue-router'
import type { DupAuthority } from '@/types/router/auth'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { useUserAttributes } from '@/features/miiaFw/composables/useUserAttributes'
import { routes } from '.'
import type { RouterPath } from '@/types/router/path'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { FeatureOptions } from '@/types/api/common/governmentSetting/queryGovernmentSettingResponseParam'

export const useRouterUtils = () => {
  const governmentStore = useGovernmentSettingStore()
  const { getUserAttributesFromStore } = useUserAttributes()
  const router = useRouter()

  /**
   * 自治体設定の機能オプションとアカウントに付与された権限でアクセス可能なパス定義を取得する
   * @param authority 権限情報
   * @returns アクセス可能なパス定義のリスト
   */
  const getAuthorizedPaths = (
    authorities: DupAuthority[],
    routerPath?: RouterPath
  ): RouteRecordRaw[] => {
    // router定義より権限設定されているページを抽出
    const guardRequiredRoutes = routes.filter(
      route => route.meta?.requiredAuthorities !== undefined
    )
    const assessableRoutes = guardRequiredRoutes.filter(guardRequiredRoute => {
      // 機能オプション
      const requiredFeatureOption = guardRequiredRoute.meta?.requiredFeatureOption as FeatureOptions | undefined
      if (requiredFeatureOption && !governmentStore.hasAuth(requiredFeatureOption)) {
        return false
      }

      // ユーザ権限
      const requiredAuthorities = guardRequiredRoute.meta?.requiredAuthorities as DupAuthority[]
      const isAuthorized = requiredAuthorities.every(requiredAuth =>
        authorities.some(
          auth =>
            requiredAuth.scope == auth.scope &&
            requiredAuth.actions.every(action => auth.actions.includes(action))
        )
      )
      return isAuthorized
    })
    return assessableRoutes.sort(a => (a.path === routerPath ? -1 : 1))
  }

  const getAuthorizedSupportPaths = async (routerPath?: RouterPath): Promise<RouteRecordRaw[]> => {
    const attribute = await getUserAttributesFromStore()
    const authority = attribute?.attribute?.['disaster-dup']?.authority ?? []
    return getAuthorizedPaths(authority, routerPath)
  }

  const transition = async (routerPath: RouterPath, params?: RouteParamsRawGeneric) => {
    const path = routerPath === '/' ? ROUTER_PATHS.orgAdminManagement : routerPath
    const authorizedPaths = await getAuthorizedSupportPaths(path)
    router.push({ name: authorizedPaths[0].name, params: params })
  }

  return {
    routes,
    getAuthorizedPaths,
    getAuthorizedSupportPaths,
    transition
  }
}
